import { composeSources } from '~/domain/composition/composeSources';
import { ISource } from '~/domain/composition/types';
import { logger } from '~/services/datadog';
import { getShouldReadOfficeDetailFromOrgService } from '~/services/posthog/FeatureFlagUtils';
import {
  MOVED_OFFICE_INFO,
  MOVED_OFFICE_TOP_LEVEL,
  TAuthMeResponse,
  IOfficeContractResponse,
  TSelectedOffice,
} from './types';

/**
 * Overlay the /office subset onto the /auth/me response.
 *
 * Takes ONLY the explicitly-declared moved fields (`MOVED_OFFICE_TOP_LEVEL` /
 * `MOVED_OFFICE_INFO`) from `office` — deliberately NOT `{...office}`. A blind spread would
 * copy whatever keys the response actually carries at runtime (types don't strip extras),
 * so an unexpected/extra key from a still-evolving org-service contract could clobber an
 * identity/session field on `/auth/me`. Explicit field-by-field enforces the AC ("office
 * subset enters state ONLY from /office") at runtime, not just in the types. `office_info`
 * keeps `/auth/me`'s stayed subfields (gotuTerms/psaTerms/education) and overlays only the
 * moved ones. The `as TSelectedOffice` cast covers the lossy `childOffices` slot (flag-ON
 * children are slim IOfficeContractChild objects filling the IUser[] shape).
 */
export function mergeOfficeDetail(
  authMe: TAuthMeResponse,
  office: IOfficeContractResponse,
): TSelectedOffice {
  const merged: Record<string, unknown> = { ...authMe };
  // `key in office` keeps the /auth/me value ONLY when /office genuinely omits a key
  // (partial/malformed response). A legitimate clear arrives as key-present-with-undefined
  // (buildOfficeContract emits every contract key), so it still overwrites here — the guard
  // is a partial-response defense, not a "prefer /auth/me" rule.
  MOVED_OFFICE_TOP_LEVEL.forEach((key) => {
    if (key in office) {
      merged[key] = office[key];
    }
  });

  const officeInfo: Record<string, unknown> = { ...authMe.office_info };
  // `office.office_info ?? {}`: a partial-but-200 /office response (missing office_info) must
  // degrade gracefully, NOT throw. Load-bearing now that flag ON has no /auth/me office
  // fallback — a data-shape bug on an up /office can't be allowed to nuke the whole read.
  const officeInfoSource: Record<string, unknown> =
    (office.office_info as Record<string, unknown> | undefined) ?? {};
  MOVED_OFFICE_INFO.forEach((key) => {
    if (key in officeInfoSource) {
      officeInfo[key] = officeInfoSource[key];
    }
  });
  merged.office_info = officeInfo;

  return merged as unknown as TSelectedOffice;
}

/**
 * Return the `/auth/me` response with the moved OFFICE-PROFILE fields removed, so under
 * flag ON office-profile data enters state ONLY from `/office` — a faithful simulation of
 * the slimmed-`/auth/me` world (and the AC guardrail at runtime, not just in the types).
 *
 * `childOffices` is intentionally RETAINED even though it's a moved field: the
 * `AUTH_ME_REQUEST` reducer hard-depends on it (switcher list + `selectedChildOffice`
 * derivation), so stripping it would break boot. Cutting the switcher LIST over to `/office`
 * is a separate step. `office_info` keeps its stayed subfields (gotuTerms/psaTerms/education);
 * only the moved subfields are removed.
 */
export function stripMovedOfficeFields(authMe: TAuthMeResponse): TAuthMeResponse {
  const stripped: Record<string, unknown> = { ...authMe };
  MOVED_OFFICE_TOP_LEVEL.forEach((key) => {
    if (key !== 'childOffices') {
      delete stripped[key];
    }
  });
  const officeInfo = stripped.office_info;
  if (officeInfo && typeof officeInfo === 'object') {
    const trimmed: Record<string, unknown> = { ...(officeInfo as Record<string, unknown>) };
    MOVED_OFFICE_INFO.forEach((key) => delete trimmed[key]);
    stripped.office_info = trimmed;
  }
  return stripped as unknown as TAuthMeResponse;
}

/**
 * Single flag-gated office-read boundary, built on `composeSources`. Both the saga path and
 * the RQ path call this with their own `/office` fetcher so the merge + flag decision live in
 * exactly one place.
 *
 * Flag ON = the REAL cutover, not an additive overlay: `/office` is the SOLE source of the
 * office subset. `/auth/me`'s office fields are stripped upstream (authMeRequest) and the
 * `office` source is `required`, so a `/office` OUTAGE (5xx/timeout) FAILS the read — into the
 * app's existing office-read failure path — rather than silently falling back to `/auth/me`.
 * The flag itself (ramped + instantly rollbackable) is the safety valve, not a per-request
 * fallback that would mask whether `/office` actually works. This makes the ramp a genuine
 * dress rehearsal for the backend `/auth/me` slim.
 *
 * Flag OFF: `/office` is skipped and `/auth/me` is returned untouched (today's behavior).
 *
 * Sources:
 *  - `authMe` (required) — passed as a promise so it runs CONCURRENTLY with `/office`.
 *  - `office` (flag+officeId-gated; `required` when the flag is ON) — org-service `/office`.
 *    On failure it emits `logger.error` (Datadog Logs; RUM is 0%-sampled) then re-throws,
 *    which now fails the read instead of degrading. (A partial-but-200 response is handled
 *    separately by the `office_info` guard in `mergeOfficeDetail` — that's a data-shape bug,
 *    not an outage, so it degrades gracefully.)
 */
export function getSelectedOfficeWithDetail(
  authMe: Promise<TAuthMeResponse>,
  officeId: string | undefined,
  fetchOfficeDetail: (officeId: string) => Promise<IOfficeContractResponse>,
): Promise<TSelectedOffice> {
  const officeReadEnabled = getShouldReadOfficeDetailFromOrgService();
  const sources: ISource<unknown>[] = [
    { id: 'authMe', required: true, fetch: () => authMe },
    {
      id: 'office',
      enabled: () => officeReadEnabled && Boolean(officeId),
      required: officeReadEnabled,
      fetch: () =>
        fetchOfficeDetail(officeId as string).catch((error) => {
          logger.error('org-service /office read failed', { officeId, error });
          throw error;
        }),
    },
  ];

  return composeSources<TSelectedOffice>(sources, (results) => {
    const authMeResponse = results.authMe as TAuthMeResponse;
    const office = results.office as IOfficeContractResponse | null;
    if (office) {
      return mergeOfficeDetail(authMeResponse, office);
    }
    // `office` is null only when the source is DISABLED (flag OFF, or flag ON with no
    // officeId) — a required `/office` failure under flag ON rejects before reconcile. Under
    // flag ON, still don't leak `/auth/me` office; flag OFF returns it untouched.
    return (
      officeReadEnabled ? stripMovedOfficeFields(authMeResponse) : authMeResponse
    ) as unknown as TSelectedOffice;
  });
}
